import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../services/api';
import { Plus, Edit, Trash2, Upload, Download, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const SAMPLE_CSV_URL = "https://customer-assets.emergentagent.com/job_quote-level-tracker/artifacts/tzoduk3d_Supplier_Data_CSV_Sample.csv";

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const fileInputRef = useRef(null);
  const categoryInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    email2: '',
    contact_person: '',
    phone: '',
    product_types: '',
    categories: [],  // Multiple categories
  });
  const [categoryInput, setCategoryInput] = useState('');

  useEffect(() => {
    fetchSuppliers();
  }, []);

  useEffect(() => {
    // Filter suppliers based on search query
    if (!searchQuery.trim()) {
      setFilteredSuppliers(suppliers);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = suppliers.filter(s => 
        s.name.toLowerCase().includes(query) ||
        s.category?.toLowerCase().includes(query) ||
        s.categories?.some(c => c.toLowerCase().includes(query)) ||
        s.product_types?.some(pt => pt.toLowerCase().includes(query))
      );
      setFilteredSuppliers(filtered);
    }
  }, [searchQuery, suppliers]);

  const fetchSuppliers = async () => {
    try {
      const response = await apiClient.get('/suppliers');
      setSuppliers(response.data);
      setFilteredSuppliers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error('Failed to load suppliers');
      setLoading(false);
    }
  };

  const fetchCategorySuggestions = async (query) => {
    try {
      const response = await apiClient.get(`/suppliers/categories/suggest?q=${encodeURIComponent(query)}`);
      setCategorySuggestions(response.data.suggestions || []);
      setShowCategorySuggestions(true);
    } catch (error) {
      console.error('Error fetching category suggestions:', error);
    }
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setCategoryInput(value);
    if (value.length >= 1) {
      fetchCategorySuggestions(value);
    } else {
      setCategorySuggestions([]);
      setShowCategorySuggestions(false);
    }
  };

  const addCategory = (category) => {
    if (category && !formData.categories.includes(category)) {
      setFormData({ ...formData, categories: [...formData.categories, category] });
    }
    setCategoryInput('');
    setShowCategorySuggestions(false);
  };

  const removeCategory = (categoryToRemove) => {
    setFormData({ 
      ...formData, 
      categories: formData.categories.filter(c => c !== categoryToRemove) 
    });
  };

  const selectCategory = (category) => {
    addCategory(category);
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await apiClient.post('/suppliers/bulk-upload', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(response.data.message);
      fetchSuppliers();
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        product_types: formData.product_types ? formData.product_types.split(',').map(pt => pt.trim()) : [],
        categories: formData.categories,
        category: formData.categories.length > 0 ? formData.categories[0] : '',  // Legacy support
      };

      if (editingSupplier) {
        await apiClient.put(`/suppliers/${editingSupplier.id}`, payload);
        toast.success('Supplier updated successfully');
      } else {
        await apiClient.post('/suppliers', payload);
        toast.success('Supplier added successfully');
      }

      fetchSuppliers();
      setShowDialog(false);
      resetForm();
    } catch (error) {
      console.error('Error saving supplier:', error);
      toast.error(error.response?.data?.detail || 'Failed to save supplier');
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    // Support both old single category and new multiple categories
    const categories = supplier.categories || (supplier.category ? [supplier.category] : []);
    setFormData({
      name: supplier.name,
      email: supplier.email,
      email2: supplier.email2 || '',
      contact_person: supplier.contact_person || '',
      phone: supplier.phone || '',
      product_types: supplier.product_types.join(', '),
      categories: categories,
    });
    setCategoryInput('');
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;

    try {
      await apiClient.delete(`/suppliers/${id}`);
      toast.success('Supplier deleted successfully');
      fetchSuppliers();
    } catch (error) {
      console.error('Error deleting supplier:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete supplier');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      email2: '',
      contact_person: '',
      phone: '',
      product_types: '',
      categories: [],
    });
    setEditingSupplier(null);
    setCategoryInput('');
    setCategorySuggestions([]);
    setShowCategorySuggestions(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="suppliers-title">Suppliers</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your supplier database</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open(SAMPLE_CSV_URL, '_blank')} data-testid="download-sample-btn">
            <Download size={18} />
            Sample CSV
          </Button>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-csv-btn"
          >
            <Upload size={18} />
            {uploading ? 'Uploading...' : 'Upload CSV'}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            className="hidden"
            onChange={handleCSVUpload}
          />
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-supplier-btn">
            <Plus size={18} />
            Add Supplier
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
        <Input
          placeholder="Search by Name, Category, or Product Type..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 pr-10"
          data-testid="supplier-search-input"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {searchQuery && (
        <p className="text-sm text-slate-500">
          Showing {filteredSuppliers.length} of {suppliers.length} suppliers
        </p>
      )}

      {/* Suppliers Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200" data-testid="suppliers-table">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Product Types</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">L1 Count</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Response Rate</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filteredSuppliers.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-slate-500">
                  {searchQuery ? 'No suppliers match your search' : 'No suppliers yet. Add your first supplier!'}
                </td>
              </tr>
            ) : (
              filteredSuppliers.map((supplier) => (
                <tr key={supplier.id} className="hover:bg-slate-50" data-testid={`supplier-row-${supplier.id}`}>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-900">{supplier.name}</div>
                    {supplier.contact_person && (
                      <div className="text-xs text-slate-500">{supplier.contact_person}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-600">{supplier.email}</div>
                    {supplier.email2 && (
                      <div className="text-xs text-slate-400 mt-1">{supplier.email2}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1 max-w-40">
                      {(supplier.categories && supplier.categories.length > 0) ? (
                        supplier.categories.map((cat, idx) => (
                          <span key={idx} className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {cat}
                          </span>
                        ))
                      ) : supplier.category ? (
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {supplier.category}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">Uncategorized</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1 max-w-48">
                      {supplier.product_types && supplier.product_types.length > 0 ? (
                        supplier.product_types.map((type, idx) => (
                          <span key={idx} className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                            {type}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      {supplier.l1_count || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-600">
                      {supplier.rfq_sent_count ? 
                        `${Math.round((supplier.response_count / supplier.rfq_sent_count) * 100)}%` : 
                        'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(supplier)} data-testid={`edit-supplier-${supplier.id}`}>
                        <Edit size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(supplier.id)} className="text-red-600" data-testid={`delete-supplier-${supplier.id}`}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowDialog(open); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="supplier-dialog">
          <DialogHeader>
            <DialogTitle>{editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
            <DialogDescription>
              {editingSupplier ? 'Update supplier information' : 'Enter supplier details below'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Supplier Name *</Label>
              <Input
                id="name"
                data-testid="supplier-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Primary Email *</Label>
              <Input
                id="email"
                type="email"
                data-testid="supplier-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="email2">Secondary Email (Optional)</Label>
              <Input
                id="email2"
                type="email"
                data-testid="supplier-email2-input"
                placeholder="Alternate email address"
                value={formData.email2}
                onChange={(e) => setFormData({ ...formData, email2: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="contact_person">Contact Person</Label>
              <Input
                id="contact_person"
                data-testid="supplier-contact-input"
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                data-testid="supplier-phone-input"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div className="relative">
              <Label htmlFor="category">Categories (add multiple)</Label>
              {/* Display selected categories as tags */}
              {formData.categories.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2 p-2 bg-slate-50 rounded-md">
                  {formData.categories.map((cat, idx) => (
                    <span key={idx} className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {cat}
                      <button
                        type="button"
                        onClick={() => removeCategory(cat)}
                        className="ml-1 text-blue-600 hover:text-red-600"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <Input
                id="category"
                ref={categoryInputRef}
                data-testid="supplier-category-input"
                value={categoryInput}
                onChange={handleCategoryChange}
                onFocus={() => categoryInput && fetchCategorySuggestions(categoryInput)}
                onBlur={() => setTimeout(() => setShowCategorySuggestions(false), 200)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && categoryInput.trim()) {
                    e.preventDefault();
                    addCategory(categoryInput.trim());
                  }
                }}
                placeholder="Type category and press Enter..."
                autoComplete="off"
              />
              {showCategorySuggestions && categorySuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-y-auto">
                  {categorySuggestions.map((cat, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 focus:bg-slate-100"
                      onMouseDown={() => selectCategory(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500 mt-1">Press Enter to add new category, or select from suggestions</p>
            </div>
            <div>
              <Label htmlFor="product_types">Product Types (comma-separated)</Label>
              <Input
                id="product_types"
                data-testid="supplier-products-input"
                value={formData.product_types}
                onChange={(e) => setFormData({ ...formData, product_types: e.target.value })}
                placeholder="e.g., Motors, Pumps, Valves"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { resetForm(); setShowDialog(false); }}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-supplier-btn">
                {editingSupplier ? 'Update Supplier' : 'Add Supplier'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Suppliers;
